import * as XLSX from 'xlsx-js-style';

const THIN_BORDER = {
  top: { style: 'thin', color: { rgb: 'B0B0B0' } },
  bottom: { style: 'thin', color: { rgb: 'B0B0B0' } },
  left: { style: 'thin', color: { rgb: 'B0B0B0' } },
  right: { style: 'thin', color: { rgb: 'B0B0B0' } },
};

const HEADER_STYLE = {
  font: { bold: true, color: { rgb: '1F4E79' } },
  fill: { patternType: 'solid', fgColor: { rgb: 'D6EAF8' } },
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
  border: THIN_BORDER,
};

const DATA_STYLE = {
  alignment: { horizontal: 'left', vertical: 'center', wrapText: true },
  border: THIN_BORDER,
};

const CENTER_DATA_STYLE = {
  ...DATA_STYLE,
  alignment: { horizontal: 'center', vertical: 'center', wrapText: true },
};

const DATE_TIME_FORMAT = 'dd-mm-yyyy hh:mm:ss';

/** Status / placeholder labels that must never be parsed as dates. */
const PLACEHOLDER_RE = /^(not logged(\s*in)?|active session|logged out|logged in|no duration)$/i;

/** ISO date or datetime (with optional fractional seconds / timezone). */
const ISO_DATE_RE =
  /^\d{4}-\d{2}-\d{2}(?:[T\s]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?)?$/;

/** Local datetime: dd-mm-yyyy or dd/mm/yyyy with time. */
const LOCAL_DATE_TIME_RE =
  /^\d{1,2}[-/]\d{1,2}[-/]\d{4}[ T]\d{1,2}:\d{2}(?::\d{2})?$/;

/** Pure clock time — kept as text to avoid Excel #VALUE! / serial confusion. */
const TIME_ONLY_RE = /^\d{1,2}:\d{2}(?::\d{2})?$/;

/**
 * Convert a real JS Date to an Excel serial (local wall time).
 * Written as numeric cells with a date format — never `t: 'd'`.
 */
function toExcelSerial(date) {
  const y = date.getFullYear();
  const m = date.getMonth();
  const d = date.getDate();
  const hh = date.getHours();
  const mm = date.getMinutes();
  const ss = date.getSeconds();
  const utcMs = Date.UTC(y, m, d, hh, mm, ss);
  return utcMs / 86400000 + 25569;
}

/**
 * Only accept Date objects or clear datetime strings. Never bare numbers / placeholders.
 * @returns {{ kind: 'date'|'text', value: Date|string }}
 */
export function coerceExportCell(value) {
  if (value == null || value === '') {
    return { kind: 'text', value: '' };
  }

  if (value instanceof Date) {
    return Number.isNaN(value.getTime())
      ? { kind: 'text', value: '' }
      : { kind: 'date', value };
  }

  // Numbers are never dates (S.NO, counts, etc.)
  if (typeof value === 'number') {
    return { kind: 'text', value: String(value) };
  }

  const s = String(value).trim();
  if (!s) return { kind: 'text', value: '' };
  if (PLACEHOLDER_RE.test(s)) return { kind: 'text', value: s };
  if (TIME_ONLY_RE.test(s)) return { kind: 'text', value: s };

  if (ISO_DATE_RE.test(s) || LOCAL_DATE_TIME_RE.test(s)) {
    const parsed = new Date(s);
    if (!Number.isNaN(parsed.getTime())) {
      return { kind: 'date', value: parsed };
    }
  }

  return { kind: 'text', value: s };
}

function buildCell(raw, { forceText = false, forceNumber = false, allowDate = false, center = false } = {}) {
  const baseStyle = center ? CENTER_DATA_STYLE : DATA_STYLE;

  if (forceNumber) {
    const n = typeof raw === 'number' ? raw : Number(raw);
    return {
      t: 'n',
      v: Number.isFinite(n) ? n : 0,
      s: baseStyle,
    };
  }

  if (forceText || !allowDate) {
    return {
      t: 's',
      v: raw == null ? '' : String(raw),
      s: baseStyle,
    };
  }

  const coerced = coerceExportCell(raw);
  if (coerced.kind === 'date') {
    return {
      t: 'n',
      v: toExcelSerial(coerced.value),
      z: DATE_TIME_FORMAT,
      s: {
        ...baseStyle,
        alignment: { ...baseStyle.alignment, horizontal: 'center' },
      },
    };
  }

  return {
    t: 's',
    v: coerced.value,
    s: baseStyle,
  };
}

function autoColWidth(headers, rows) {
  return headers.map((header, colIndex) => {
    let max = String(header ?? '').length;
    rows.forEach((row) => {
      const cell = row[colIndex];
      const len = cell == null ? 0 : String(cell).length;
      if (len > max) max = len;
    });
    return { wch: Math.min(Math.max(max + 2, 12), 45) };
  });
}

/**
 * Download a professionally styled .xlsx workbook (xlsx-js-style).
 *
 * @param {Object} opts
 * @param {string} opts.fileName
 * @param {string} opts.sheetName
 * @param {string[]} opts.headers
 * @param {any[][]} opts.rows
 * @param {number[]} [opts.textColumns] - force text (usernames, emails, labels)
 * @param {number[]} [opts.numberColumns] - force plain numbers (S.NO) — never date-formatted
 * @param {number[]} [opts.dateColumns] - only these cols may become Excel date/times
 * @param {number[]} [opts.centerColumns]
 */
export function downloadStyledXlsx({
  fileName,
  sheetName = 'Report',
  headers = [],
  rows = [],
  textColumns = [],
  numberColumns = [],
  dateColumns = [],
  centerColumns = [],
}) {
  const textColSet = new Set(textColumns);
  const numberColSet = new Set(numberColumns);
  const dateColSet = new Set(dateColumns);
  const centerColSet = new Set(centerColumns);
  const safeSheetName = String(sheetName || 'Report').slice(0, 31);

  const ws = {};
  const lastRow = rows.length;
  const lastCol = Math.max(headers.length - 1, 0);

  headers.forEach((header, c) => {
    const addr = XLSX.utils.encode_cell({ r: 0, c });
    ws[addr] = { t: 's', v: header, s: HEADER_STYLE };
  });

  rows.forEach((row, r) => {
    headers.forEach((_, c) => {
      const addr = XLSX.utils.encode_cell({ r: r + 1, c });
      const forceNumber = numberColSet.has(c);
      const forceText = textColSet.has(c) || (!forceNumber && !dateColSet.has(c));
      ws[addr] = buildCell(row[c], {
        forceText,
        forceNumber,
        allowDate: dateColSet.has(c),
        center: centerColSet.has(c),
      });
    });
  });

  ws['!ref'] = XLSX.utils.encode_range({
    s: { r: 0, c: 0 },
    e: { r: lastRow, c: lastCol },
  });
  ws['!cols'] = autoColWidth(headers, rows);
  ws['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: lastRow, c: lastCol },
    }),
  };
  ws['!views'] = [{
    state: 'frozen',
    ySplit: 1,
    topLeftCell: 'A2',
    activeCell: 'A2',
  }];

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, safeSheetName);

  // cellDates:false — we emit numeric serials ourselves for date columns only
  const excelBuffer = XLSX.write(wb, {
    bookType: 'xlsx',
    type: 'array',
    cellDates: false,
  });
  const blob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName.endsWith('.xlsx') ? fileName : `${fileName}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
